/**
 * Tier-1 OS sandbox for the real `Shell` tool (mind/03-roadmap.md "capability tiers").
 *
 * Wraps the spawned `/bin/sh` in the platform's process sandbox so a hostile/buggy command
 * can read the machine but can only WRITE inside an allowlist (cwd + tmp + extra `writePaths`),
 * and gets no network unless granted:
 *   - macOS: `sandbox-exec` (seatbelt) with a generated profile
 *   - Linux: `bwrap` (bubblewrap) with `--ro-bind / /` + writable binds
 *
 * Pure argv builders (unit-testable, no node imports) + an async wrapper-binary locator.
 * This complements — does not replace — env secret-scrubbing and the permission prompt:
 * the FS jail can't contain a real process; this makes the *process* itself contained.
 */

export class OsSandboxOptions {
  /** Allow outbound network. Default OFF (Tier-1: no network unless granted). */
  network = false;
  /** Extra absolute paths writable beyond cwd + tmp (e.g. a build cache). */
  writePaths: string[] = [];
}

export interface SandboxWrap {
  bin: string;
  args: string[]; // full argv: wrapper flags + /bin/sh -c <command>
}

/** Writable allowlist shared by both platforms: cwd, the tmp roots, /dev. */
function writable(cwd: string, o: OsSandboxOptions, tmpDir?: string): string[] {
  const set = new Set<string>([cwd, '/tmp', '/private/tmp', '/private/var/folders', '/dev', ...(tmpDir ? [tmpDir] : []), ...o.writePaths]);
  return [...set];
}

const sbQuote = (p: string) => `"${p.replace(/(["\\])/g, '\\$1')}"`;

/** macOS seatbelt profile: allow everything, then deny writes/network, then re-allow the allowlist
 *  (seatbelt resolves conflicts by specificity, so subpath allows override the broad deny). */
export function seatbeltProfile(cwd: string, o: OsSandboxOptions, tmpDir?: string): string {
  const allows = writable(cwd, o, tmpDir).map((p) => `(subpath ${sbQuote(p)})`).join(' ');
  return [
    '(version 1)',
    '(allow default)',
    ...(o.network ? [] : ['(deny network*)']),
    '(deny file-write*)',
    `(allow file-write* ${allows})`,
  ].join('\n');
}

/** Build the wrapped argv for `sh -c <command>`, or null if `platform` has no supported wrapper. */
export function sandboxArgv(command: string, cwd: string, opts: Partial<OsSandboxOptions> = {}, platform: string = process.platform, tmpDir?: string): SandboxWrap | null {
  const o = { ...new OsSandboxOptions(), ...opts };
  if (platform === 'darwin') {
    return { bin: '/usr/bin/sandbox-exec', args: ['-p', seatbeltProfile(cwd, o, tmpDir), '/bin/sh', '-c', command] };
  }
  if (platform === 'linux') {
    const binds = writable(cwd, o, tmpDir).filter((p) => p !== '/dev' && !p.startsWith('/private')).flatMap((p) => ['--bind-try', p, p]);
    return {
      bin: 'bwrap',
      args: ['--ro-bind', '/', '/', ...binds, '--dev', '/dev', '--proc', '/proc', '--die-with-parent', ...(o.network ? [] : ['--unshare-net']), '/bin/sh', '-c', command],
    };
  }
  return null;
}

/** Locate the wrapper binary for this platform; null = sandboxing unavailable here. */
export async function findSandboxWrapper(platform: string = process.platform): Promise<string | null> {
  const { existsSync } = await import('node:fs');
  if (platform === 'darwin') return existsSync('/usr/bin/sandbox-exec') ? '/usr/bin/sandbox-exec' : null;
  if (platform === 'linux') {
    for (const dir of (process.env.PATH ?? '/usr/bin:/bin').split(':')) if (dir && existsSync(`${dir}/bwrap`)) return `${dir}/bwrap`;
    return null;
  }
  return null;
}
